/**
 * JoinWaitlist.js
 * Form for users to join the Constellation KG waitlist
 *
 * PRIVACY NOTE:
 * We intentionally do not ask users why they want to join.
 * We only collect the minimum information needed: name and email.
 *
 * Renders on the sky (SkyPage) in a pale teal card. Fields, handlers, API call
 * and both navigation buttons are unchanged from the white-card version — only
 * the markup's class names and the wrapper moved. Styles come from SkyForm.css.
 *
 * JoinWaitlist.css is still imported, deliberately: its `.back-button`,
 * `.error-message`, `.form-field`, `.form-actions`, `.primary-button` and
 * `.secondary-button` rules are relied on by PrivacyPolicy, TermsOfService,
 * MyAccount, Journal and Admin through the global CSS bundle. See docs/TODO.md.
 *
 * This file lives under Admin/ for historical reasons — it is a public page, not
 * an admin one. Noted in docs/TODO.md rather than moved, since moving it would
 * touch the import in App.js.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SkyPage from '../LandingSky/SkyPage';
import '../LandingSky/SkyForm.css';
import './JoinWaitlist.css';
import { apiService } from '../../services/api';

const JoinWaitlist = () => {
  const navigate = useNavigate();

  // =========================================
  // State Management
  // =========================================

  // Form state - only essential fields
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: ''
  });

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // =========================================
  // Event Handlers
  // =========================================

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await apiService.waitlist.join(formData);
      setSuccess(true);
    } catch (error) {
      console.error('Waitlist join error:', error);
      setError(error.response?.data?.message || error.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  // =========================================
  // Success State Render
  // =========================================

  if (success) {
    return (
      <SkyPage>
        <main className="sky-form-region">
          <div className="sky-form-spacer-top" />

          <div className="sky-form-card">
            <h1 className="sky-form-heading">Thank you for joining our waitlist!</h1>
            <p className="sky-form-lead">
              We've received your request to join Constellation KG.
              We'll review your application and notify you when you've been approved
              for alpha testing.
            </p>
            <button
              onClick={handleBackToHome}
              className="sky-form-primary"
            >
              Back to Home
            </button>
          </div>

          <div className="sky-form-spacer-bottom" />
        </main>
      </SkyPage>
    );
  }

  // =========================================
  // Form Render
  // =========================================

  return (
    <SkyPage>
      <main className="sky-form-region">
        <div className="sky-form-spacer-top" />

        <div className="sky-form-card">
          <form onSubmit={handleSubmit} className="sky-form-body">
            <h1 className="sky-form-heading">Join the Waitlist</h1>
            <p className="sky-form-lead">
              Please fill out this form to join our waitlist for alpha testing.
            </p>

            {error && <div className="sky-form-error" role="alert">{error}</div>}

            <div className="sky-form-field">
              <label className="sky-form-label" htmlFor="email">Email *</label>
              <input
                className="sky-form-input"
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="sky-form-field">
              <label className="sky-form-label" htmlFor="firstName">First Name *</label>
              <input
                className="sky-form-input"
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="sky-form-field">
              <label className="sky-form-label" htmlFor="lastName">Last Name *</label>
              <input
                className="sky-form-input"
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={isLoading}
              />
            </div>

            <div className="sky-form-actions">
              <button
                type="submit"
                disabled={isLoading}
                aria-busy={isLoading}
                className="sky-form-primary"
              >
                {isLoading ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button
                type="button"
                onClick={handleBackToLogin}
                disabled={isLoading}
                className="sky-form-secondary"
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>

        <div className="sky-form-spacer-bottom" />
      </main>
    </SkyPage>
  );
};

export default JoinWaitlist;
